import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../config';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import MuiButton from '@mui/material/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { modalStyle } from '@/views/Clients/Claim';
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import { formatTimestamp } from '@/utils/date/date';
import { useTranslation } from 'react-i18next';
import loader from '../../../assets/img/loader.svg';

const DomainsTab = ({ organizationId }) => {
  const { t } = useTranslation();
  const [domains, setDomains] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [modalOpen, setModalOpen] = useState(false);
  const [domainName, setDomainName] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalDomainInfoOpen, setModalDomainInfoOpen] = useState(false);
  const [modalDomainName, setModalDomainName] = useState('');
  const [modalDomainInfo, setModalDomainInfo] = useState({});
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getOrganizationDomains(organizationId);
        const state = localStorage['appState'];

        if (state) {
          const AppState = JSON.parse(state);
          if (AppState?.user?.role !== 'admin') {
            window.location.href = '/client/error';
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
    setLoaded(true);
  }, [organizationId]);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getOrganizationDomains = async (orgId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/admin/getOrganizationDomains/${orgId}`,
      );
      setDomains(res.data.domains);
    } catch (error) {
      console.error(error);
    }
  };

  const openDomainInfoModal = async (domain) => {
    try {
      setModalDomainName(domain);
      const res = await axios.get(
        `${API_BASE_URL}/admin/getCustomDomainInfo/${domain}`,
      );
      setModalDomainInfoOpen(true);
      setModalDomainInfo(res.data.domainInfo);
    } catch (err) {
      console.error(err);
    }
  };

  const addDomain = async (e) => {
    e.preventDefault();
    setAddingDomain(true);
    try {
      await axios.post(`${API_BASE_URL}/admin/addOrganizationDomain`, {
        organizationId,
        domain: domainName,
      });
      setModalOpen(false);
      setDomainName('');
      await getOrganizationDomains(organizationId);
    } catch (err) {
      setModalError(
        err.response?.data?.msg || 'Could not add domain to organization!',
      );
    } finally {
      setAddingDomain(false);
    }
  };

  const deleteDomain = async (id) => {
    if (window.confirm(t('organizations.update.confirmations.deleteDomain'))) {
      setLoaded(true);
      try {
        const state = localStorage['appState'];
        const AppState = state ? JSON.parse(state) : null;
        const token = AppState?.user?.accessToken;
        const headers = { Authorization: `Bearer ${token}` };

        await axios.delete(
          `${API_BASE_URL}/admin/deleteOrganizationDomain/${id}`,
          { headers },
        );
        await getOrganizationDomains(organizationId);
      } catch (err) {
        console.error(err);
      } finally {
        setLoaded(false);
      }
    }
  };

  const closeDomainInfoModal = () => {
    setModalDomainInfoOpen(false);
    setDomainName('');
    setModalDomainInfo({});
  };

  const copyToClipboard = async (record, recordType, index) => {
    try {
      const dnsType = recordType === 'CNAME' ? 'CNAME' : 'TXT';
      const textToCopy = `Type: ${dnsType}\nDomain: ${record.name}\nValue: ${record.value}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedIndex(index);

      setTimeout(() => {
        setCopiedIndex(null);
      }, 5000);
    } catch (err) {
      console.error(err);
      toast.error(t('organizations.update.errors.copyFailed'));
    }
  };

  const copyAllRecords = async () => {
    try {
      const records = modalDomainInfo?.sending_dns_records || [];
      let allRecords = '';

      records.forEach((record, index) => {
        let recordType = '';
        if (record.value.includes('k=rsa')) {
          recordType = 'TXT';
        } else if (record.value.includes('v=spf')) {
          recordType = 'TXT';
        } else if (record.record_type === 'CNAME') {
          recordType = 'CNAME';
        }

        if (recordType) {
          if (index > 0) allRecords += '\n\n';
          allRecords += `Type: ${recordType}\nDomain: ${record.name}\nValue: ${record.value}`;
        }
      });

      await navigator.clipboard.writeText(allRecords);
      setCopiedAll(true);

      setTimeout(() => {
        setCopiedAll(false);
      }, 5000);
    } catch (err) {
      console.error(err);
      toast.error(t('organizations.update.errors.copyAllFailed'));
    }
  };

  const downloadAsCSV = () => {
    try {
      const records = modalDomainInfo?.sending_dns_records || [];
      let csvContent = 'Type,Domain,Value\n';

      records.forEach((record) => {
        let recordType = '';
        if (record.value.includes('k=rsa')) {
          recordType = 'TXT (DKIM)';
        } else if (record.value.includes('v=spf')) {
          recordType = 'TXT (SPF)';
        } else if (record.record_type === 'CNAME') {
          recordType = 'CNAME';
        }

        if (recordType) {
          // INFO: need to escape double quotes in values
          const escapedValue = record.value.replace(/"/g, '""');
          csvContent += `"${recordType}","${record.name}","${escapedValue}"\n`;
        }
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${modalDomainName}_dns_records.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error(t('organizations.update.errors.downloadFailed'));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setDomainName('');
    setModalError('');
  };

  const openAddDomainModal = () => {
    setModalOpen(true);
    setModalError('');
  };
  if (!loaded) {
    return (
      <div className='loaders' style={{ marginTop: '20px' }}>
        <img alt={'loader'} src={loader} />
      </div>
    );
  }
  return (
    <>
      <GridContainer style={{ paddingTop: '30px' }}>
        <GridItem xs={12} sm={12} md={12}>
          <Button onClick={openAddDomainModal} className='link' color='primary'>
            {t('organizations.update.addDomainButton')}
          </Button>
        </GridItem>

        <Table width='100%' id='template'>
          <TableHead>
            <TableRow>
              <TableCell align='left'>
                {t('organizations.update.table.nameColumn')}
              </TableCell>
              <TableCell align='left'>
                {t('organizations.update.table.statusColumn')}
              </TableCell>
              <TableCell align='left'>
                {t('organizations.update.table.createdByColumn')}
              </TableCell>
              <TableCell align='left'>
                {t('organizations.update.table.createdAtColumn')}
              </TableCell>
              <TableCell align='left'>
                {t('organizations.update.table.actionsColumn')}
              </TableCell>
            </TableRow>
          </TableHead>

          {domains && domains.length > 0 ? (
            <TableBody>
              {(rowsPerPage > 0
                ? domains.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  )
                : domains
              ).map((obj, index) => (
                <TableRow className='txtalign' key={index}>
                  <TableCell width='20%'>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <b>{obj && obj.domain}</b>
                      <Link
                        to='#'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={() => openDomainInfoModal(obj.domain)}>
                        <InfoIcon />
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell>
                    <b>{obj && obj.domain_status}</b>
                  </TableCell>
                  <TableCell width='25%'>{obj && obj.created_by}</TableCell>
                  <TableCell width='25%'>
                    {obj && formatTimestamp(obj.created_at)}
                  </TableCell>
                  <TableCell width='10%'>
                    {obj && obj.id && (
                      <Link
                        to='#'
                        title='Delete Organization Domain'
                        onClick={() => deleteDomain(obj.id)}>
                        <span className='material-icons'>delete</span>
                      </Link>
                    )}{' '}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow className='txtalign'>
                <TableCell colSpan={7} align='center'>
                  {t('organizations.update.table.noDomains')}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          <TableFooter>
            <TableRow align='center'>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={7}
                count={domains.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { roboto: 'rows per page' },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </GridContainer>
      <Modal open={modalOpen} onClose={closeModal}>
        <form onSubmit={addDomain}>
          <Box
            sx={modalStyle}
            className='makeStyles-card-96'
            noValidate
            autoComplete='off'>
            <CardHeader
              style={{ transform: 'translateY(-50%)' }}
              color='primary'>
              <h4>{t('organizations.update.modal.addDomainTitle')}</h4>
            </CardHeader>
            <CardBody style={{ marginTop: '-20px' }}>
              {modalError && (
                <p
                  style={{
                    color: 'red',
                    padding: '0',
                    margin: 0,
                    fontSize: '15px',
                  }}>
                  {modalError}
                </p>
              )}
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    type='text'
                    labelText={t('organizations.update.modal.nameLabel')}
                    onChange={(e) => setDomainName(e.target.value)}
                    autoComplete='off'
                    formControlProps={{
                      fullWidth: true,
                    }}
                  />
                </GridItem>
                <main
                  style={{
                    padding: '20px',
                    gap: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}></main>
              </GridContainer>
              <div>
                <GridContainer>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={addingDomain || !domainName.trim()}
                    style={{ margin: '5px 20px', minWidth: '100px' }}>
                    {addingDomain ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : (
                      t('common.buttons.add')
                    )}
                  </Button>
                  <Button
                    variant='contained'
                    color='info'
                    onClick={closeModal}
                    disabled={addingDomain}
                    style={{ margin: '5px 20px' }}>
                    {t('common.buttons.close')}
                  </Button>
                </GridContainer>
              </div>
            </CardBody>
          </Box>
        </form>
      </Modal>
      <Modal
        open={modalDomainInfoOpen}
        onClose={closeDomainInfoModal}
        loaded={!!modalDomainInfo?.id}>
        <Box
          sx={{
            ...modalStyle,
            maxWidth: '900px',
            width: '90%',
          }}
          className='makeStyles-card-96'
          noValidate
          autoComplete='off'>
          <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
            <h4>{t('organizations.update.domainInfoModal.title')}</h4>
          </CardHeader>
          <CardBody style={{ marginTop: '-20px' }}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}>
                  <h3 style={{ margin: 0 }}>{modalDomainName}</h3>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                    }}>
                    <Chip
                      label={modalDomainInfo?.state || 'Unknown'}
                      color={
                        modalDomainInfo?.state === 'active'
                          ? 'success'
                          : 'warning'
                      }
                      size='small'
                    />
                    <Typography variant='caption' style={{ color: '#666' }}>
                      Created: {formatTimestamp(modalDomainInfo?.created_at)}
                    </Typography>
                  </div>
                </div>
                <Divider />
                <br />
                <Typography variant='body2' style={{ marginBottom: '16px' }}>
                  {t('organizations.update.domainInfoModal.txtRecordsInfo')}
                </Typography>

                {modalDomainInfo?.sending_dns_records &&
                modalDomainInfo.sending_dns_records.length > 0 ? (
                  <>
                    <div
                      style={{
                        marginBottom: '16px',
                        display: 'flex',
                        gap: '10px',
                      }}>
                      <MuiButton
                        variant='outlined'
                        color={copiedAll ? 'success' : 'primary'}
                        size='small'
                        onClick={copyAllRecords}
                        startIcon={
                          copiedAll ? <CheckIcon /> : <ContentCopyIcon />
                        }>
                        {copiedAll
                          ? t('organizations.update.domainInfoModal.copied')
                          : t(
                              'organizations.update.domainInfoModal.copyAllRecords',
                            )}
                      </MuiButton>
                      <MuiButton
                        variant='outlined'
                        color='primary'
                        size='small'
                        onClick={downloadAsCSV}
                        startIcon={<DownloadIcon />}>
                        {t('organizations.update.domainInfoModal.downloadCsv')}
                      </MuiButton>
                    </div>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width='15%'>
                            {t(
                              'organizations.update.domainInfoModal.table.typeColumn',
                            )}
                          </TableCell>
                          <TableCell width='25%'>
                            {t(
                              'organizations.update.domainInfoModal.table.domainColumn',
                            )}
                          </TableCell>
                          <TableCell width='45%'>
                            {t(
                              'organizations.update.domainInfoModal.table.valueColumn',
                            )}
                          </TableCell>
                          <TableCell width='10%' align='center'>
                            {t(
                              'organizations.update.domainInfoModal.table.statusColumn',
                            )}
                          </TableCell>
                          <TableCell width='5%' align='center'>
                            {t(
                              'organizations.update.domainInfoModal.table.actionsColumn',
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {modalDomainInfo.sending_dns_records.map(
                          (record, index) => {
                            let recordType = '';
                            let recordSubtype = '';
                            if (record.value.includes('k=rsa')) {
                              recordType = 'TXT';
                              recordSubtype = 'DKIM';
                            } else if (record.value.includes('v=spf')) {
                              recordType = 'TXT';
                              recordSubtype = 'SPF';
                            } else if (record.record_type === 'CNAME') {
                              recordType = 'CNAME';
                            }

                            if (!recordType) return null;

                            const isInvalid = record.valid !== 'valid';

                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <strong>{recordType}</strong>
                                  {recordSubtype && (
                                    <Typography
                                      variant='caption'
                                      display='block'
                                      style={{ color: '#666' }}>
                                      ({recordSubtype})
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    style={{ wordBreak: 'break-word' }}>
                                    {record.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    style={{
                                      wordBreak: 'break-word',
                                      fontSize: '12px',
                                      fontFamily: 'monospace',
                                      backgroundColor: '#f5f5f5',
                                      padding: '8px',
                                      borderRadius: '4px',
                                      maxHeight: '100px',
                                      overflow: 'auto',
                                    }}>
                                    {record.value}
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '4px',
                                    }}>
                                    {isInvalid && (
                                      <ErrorOutlineIcon
                                        color='error'
                                        fontSize='small'
                                      />
                                    )}
                                    <Typography
                                      variant='body2'
                                      style={{
                                        color: isInvalid
                                          ? '#d32f2f'
                                          : '#2e7d32',
                                        fontWeight: 'bold',
                                      }}>
                                      {record.valid}
                                    </Typography>
                                  </div>
                                </TableCell>
                                <TableCell align='center'>
                                  <Tooltip
                                    title={
                                      copiedIndex === index
                                        ? 'Copied!'
                                        : 'Copy DNS record'
                                    }>
                                    <IconButton
                                      size='small'
                                      onClick={() =>
                                        copyToClipboard(
                                          record,
                                          recordType,
                                          index,
                                        )
                                      }
                                      color={
                                        copiedIndex === index
                                          ? 'success'
                                          : 'primary'
                                      }>
                                      {copiedIndex === index ? (
                                        <CheckIcon fontSize='small' />
                                      ) : (
                                        <ContentCopyIcon fontSize='small' />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <Typography
                    variant='body2'
                    align='center'
                    style={{ padding: '20px' }}>
                    {t('organizations.update.domainInfoModal.noDnsRecords')}
                  </Typography>
                )}
              </GridItem>
            </GridContainer>
            <div style={{ marginTop: '32px' }}>
              <GridContainer>
                <Button
                  variant='contained'
                  color='info'
                  onClick={closeDomainInfoModal}
                  style={{ margin: '5px 20px' }}>
                  {t('common.buttons.close')}
                </Button>
              </GridContainer>
            </div>
          </CardBody>
        </Box>
      </Modal>
    </>
  );
};

export default DomainsTab;
